import {
  Box,
  Chip,
  Dropdown,
  DropdownArrow,
  DropdownContainer,
  Flex,
  Text,
} from '@instamenu/ui-system-next';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale } from '@instamenu/context-i18n';
import * as styles from './Sidebar.css';
import { clsx } from 'clsx';
import { NavItemProps } from './use-navigation';
import Link from 'next/link';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

const PendingDots = () => (
  <span style={{ display: 'inline-block', width: '3ch' }}>
    <span className={styles.pendingDot}>.</span>
    <span className={styles.pendingDot}>.</span>
    <span className={styles.pendingDot}>.</span>
  </span>
);

// Replays CSS animation when pending flips false or the label text changes.
// Uses a key to force remount, restarting the CSS animation with zero JS timers.
const AnimatedLabel = ({ label, pending }: { label: string; pending?: boolean }) => {
  const [animationKey, setAnimationKey] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prevPending = useRef(pending);
  const prevLabel = useRef(label);

  useLayoutEffect(() => {
    const didPendingComplete = Boolean(prevPending.current) && !pending;
    const didLabelChange = !pending && prevLabel.current !== label;

    if (didPendingComplete || didLabelChange) {
      setAnimationKey((key) => key + 1);
      setShouldAnimate(true);
    } else if (pending) {
      setShouldAnimate(false);
    }

    prevPending.current = pending;
    prevLabel.current = label;
  }, [label, pending]);

  if (pending) {
    return (
      <span>
        {label}
        <PendingDots />
      </span>
    );
  }

  return (
    <span key={animationKey} className={shouldAnimate ? styles.typewriter : undefined}>
      {label}
    </span>
  );
};

export const NavItem = (props: NavItemProps) => {
  const {
    label,
    path,
    icon: Icon,
    badge,
    subItems,
    isActive,
    alwaysOpen,
    headerAction,
    onClick,
    isSectionLabel,
  } = props;
  const pathname = usePathname();
  const { i18n } = useLocale();
  const [isOpen, setIsOpen] = useState(props.isOpen ?? false);
  const [openMenuPath, setOpenMenuPath] = useState<string | null>(null);
  const [menuPlacement, setMenuPlacement] = useState<'below' | 'above'>('below');
  const [renameItemKey, setRenameItemKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const hasSubItems = subItems && subItems.length > 0;
  const isActiveParent = isActive || subItems?.some((item) => pathname === item.path);

  useEffect(() => {
    if (!isOpen) setOpenMenuPath(null);
  }, [isOpen]);

  const saveRename = (item: NonNullable<NavItemProps['subItems']>[number]) => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== item.label) {
      item.onRename?.(trimmed);
    }
    setRenameItemKey(null);
  };

  return (
    <Box>
      {isSectionLabel ? (
        label ? (
          <div className={styles.sectionLabel}>{label}</div>
        ) : null
      ) : (
        <Link
          href={path}
          onClick={(e) => {
            if (onClick) {
              e.preventDefault();
              onClick();
            } else if (hasSubItems && !alwaysOpen) {
              setIsOpen(!isOpen);
              e.preventDefault();
            }
          }}
          className={clsx(styles.navItem, isActiveParent && styles.activeNavItem)}
        >
          <Flex style={{ gap: '4px', alignItems: 'center' }}>
            {Icon && <Icon size={18} />}
            <Text variant="body1">{label}</Text>
          </Flex>
          <Flex style={{ gap: '6px', alignItems: 'center' }}>
            {badge && <Chip>{badge}</Chip>}
            {hasSubItems && !alwaysOpen && <DropdownArrow isOpen={isOpen} />}
            {headerAction}
          </Flex>
        </Link>
      )}

      {hasSubItems && (
        <Box
          className={styles.subItems}
          data-open={alwaysOpen || isOpen}
          data-menu-open={openMenuPath !== null}
        >
          {subItems.map((item) => (
            <Box
              key={item.key ?? item.path}
              className={styles.subItemRow}
              data-menu-open={openMenuPath === item.path}
            >
              <Link
                className={clsx(
                  styles.subItem,
                  (item.isActive ?? pathname === item.path) && styles.activeSubItem,
                  item.pending && styles.pendingSubItem,
                  styles.link,
                )}
                href={item.path}
                tabIndex={item.pending ? -1 : undefined}
                aria-disabled={item.pending || undefined}
                onClick={(e) => {
                  if (item.pending) {
                    e.preventDefault();
                  } else if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
              >
                {item.icon && <item.icon size={14} />}
                {renameItemKey === (item.key ?? item.path) ? (
                  <input
                    className={styles.renameInput}
                    value={renameValue}
                    autoFocus
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveRename(item);
                      }
                      if (e.key === 'Escape') {
                        setRenameItemKey(null);
                      }
                    }}
                    onBlur={() => saveRename(item)}
                    onClick={(e) => e.preventDefault()}
                  />
                ) : (
                  <AnimatedLabel label={item.label} pending={item.pending} />
                )}
              </Link>

              {(item.onDelete || item.onRename) && (
                <DropdownContainer onDismiss={() => setOpenMenuPath(null)}>
                  <button
                    type="button"
                    aria-label="Thread actions"
                    className={styles.subItemAction}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPlacement(window.innerHeight - rect.bottom < 150 ? 'above' : 'below');
                      setOpenMenuPath((prev) => (prev === item.path ? null : item.path));
                    }}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  <Dropdown
                    visible={openMenuPath === item.path}
                    placement={menuPlacement}
                    align="right"
                  >
                    {item.onRename && (
                      <button
                        type="button"
                        className={styles.subItemMenuItem}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setRenameItemKey(item.key ?? item.path);
                          setRenameValue(item.label);
                          setOpenMenuPath(null);
                        }}
                      >
                        <Pencil size={14} />
                        <span>{i18n.t('mainMenu.renameChat')}</span>
                      </button>
                    )}
                    {item.onDelete && (
                      <button
                        type="button"
                        className={styles.subItemMenuItem}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onDelete?.();
                          setOpenMenuPath(null);
                        }}
                      >
                        <Trash2 size={14} />
                        <span>{i18n.t('mainMenu.deleteChat')}</span>
                      </button>
                    )}
                  </Dropdown>
                </DropdownContainer>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
